import cx from 'clsx';
import type { ReactElement, ReactNode } from 'react';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components';
import { isValidElement } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import {
  IconChevronRightOutlined,
  IconOpenlinkOutlined,
  IconPlusOutlined,
} from '@repo/monochrome-icons';
import { SkeletonLoader } from '../skeleton-loader';
import { Spinner } from '../spinner';
import styles from './selector.module.css';

export interface SelectorProps extends AriaButtonProps {
  icon?: ReactElement;
  title?: ReactNode;
  subtitle?: string | ReactElement;
  content?: string | ReactElement;
  isLoading?: boolean;
  variant?: 'classic' | 'add' | 'link';
  actionIcon?: ReactElement;
  showActionIcon?: boolean;
  showLoadingIcon?: boolean;
}

/**
 * This component is an incomplete implementation of the Qonto Selector component.
 * Though it adheres to the Qonto Selector API, it is not a full implementation.
 * It was agreed in this Slack thread (https://qonto.slack.com/archives/C06UZ4JKVHU/p1732540595204029).
 */
export function Selector({
  isDisabled = false,
  isLoading = false,
  isPending = false,
  variant = 'classic',
  className,
  icon,
  title,
  subtitle,
  content,
  onPress,
  actionIcon,
  showActionIcon = true,
  showLoadingIcon = true,
  ...props
}: SelectorProps): ReactElement {
  const renderActionIcon = (): ReactElement => {
    if (actionIcon) {
      return actionIcon;
    }

    if (isPending) {
      return <Spinner color="primary-a" data-testid="selector-spinner" size="medium" />;
    }

    if (variant === 'add') return <IconPlusOutlined />;
    if (variant === 'link') return <IconOpenlinkOutlined />;
    return <IconChevronRightOutlined />;
  };

  return (
    <AriaButton
      className={cx(
        styles.selector,
        (variant === 'classic' || variant === 'link') && styles.classic,
        variant === 'add' && styles.add,
        isDisabled && styles.disabled,
        className
      )}
      isDisabled={isDisabled}
      onPress={onPress}
      type="button"
      {...props}
      data-test-selector
    >
      {isLoading ? (
        <>
          {showLoadingIcon ? (
            <SkeletonLoader.Avatar className={styles.icon} data-test-selector-icon-loading />
          ) : null}
          <div className={styles.header}>
            <SkeletonLoader.Line className="mb-8" data-test-selector-line-loading width="90px" />
            <SkeletonLoader.Line className="mb-8" data-test-selector-line-loading width="156px" />
          </div>
          {showActionIcon ? (
            <div className={styles['action-icon']}>
              <IconChevronRightOutlined />
            </div>
          ) : null}
        </>
      ) : (
        <>
          {icon ? (
            <div className={styles.icon} data-test-selector-icon>
              {icon}
            </div>
          ) : null}
          <div className={styles.header}>
            {title ? (
              <span className={cx('body-standard', styles.title)} data-test-selector-title>
                {title}
              </span>
            ) : null}
            {subtitle ? (
              <span className={cx('body-standard', styles.subtitle)} data-test-selector-subtitle>
                {subtitle}
              </span>
            ) : null}
          </div>
          {content ? (
            <div className={cx(styles.content, 'ml-24')} data-test-selector-content>
              {isValidElement(content) ? content : <span className="body-standard">{content}</span>}
            </div>
          ) : null}
          {showActionIcon ? (
            <div className={styles['action-icon']} data-test-selector-action-icon>
              {renderActionIcon()}
            </div>
          ) : null}
        </>
      )}
    </AriaButton>
  );
}
